import type { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiError } from "../api/client";

interface Props {
  error: unknown;
  onRetry?: () => void;
  /** Force inline (banner) rendering instead of full-block. */
  inline?: boolean;
  /** Optional override of the default "Đã có lỗi" headline. */
  title?: string;
  /** Optional override of the detail line (defaults to the server's message). */
  detail?: string;
  children?: ReactNode;
}

export function ErrorState({
  error,
  onRetry,
  inline,
  title,
  detail,
  children,
}: Props) {
  const e = apiError(error);
  const headline = title ?? e.title;
  const body = detail ?? e.detail;
  if (inline) {
    return (
      <div
        className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        role="alert"
      >
        <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
        <span>
          <strong>{headline}.</strong> {body}
        </span>
        {onRetry && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRetry}
            className="ml-2 h-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            Thử lại
          </Button>
        )}
        {children}
      </div>
    );
  }
  return (
    <div
      className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed bg-card px-6 py-12 text-center"
      role="alert"
    >
      <div
        className="flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive"
        aria-hidden="true"
      >
        <AlertTriangle className="size-6" />
      </div>
      <h2 className="text-lg font-semibold text-foreground">{headline}</h2>
      <p className="max-w-md text-sm text-muted-foreground">{body}</p>
      {(onRetry || children) && (
        <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
          {onRetry && (
            <Button type="button" onClick={onRetry}>
              Thử lại
            </Button>
          )}
          {children}
        </div>
      )}
    </div>
  );
}
